import { FlowGrid, InfoSheetPanel, Paragraph, useOverlay } from '@fs/zion-ui'
import React, { useCallback, useMemo, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getNestedOrFloatingElementById, useSlsStateContext, getNestedSubelementsByType } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { EssentialInformationCard } from '../../../../components/generic/essentialInformationCard'
import { FamilyMembersCard } from '../../../../components/generic/familyMembersCard/FamilyMembersCard'
import { stepperInitialState, stepperReducer, STEPS } from '../../../../utils/stepperReducer'
import EventsCard from '../../../../components/generic/eventsCard/EventsCard'
import AdditionalFactsCard from '../../../../components/generic/additionalFactsCard/AdditionalFactsCard'
import { ADD_NAME_PANEL_REFERRERS, getHeaderDetails, redirectToSourceLinker } from './utils/utils'
import { ReviewCard } from '../../../../components/generic/reviewCard/ReviewCard'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import UnsavedChangesDialog from '../../../../components/generic/unsavedChangesDialog/UnsavedChangesDialog'
import { useDisplayHighlights } from '../../../../hooks/useDisplayHighlights'

/**
 * AddNamesPanel component for managing the step-by-step process of adding and reviewing person details,
 * including essential information, family members, events, and additional facts. Handles unsaved changes,
 * overlays, and navigation between multiple persons to review.
 *
 * @param {Object} props - Component props.
 * @param {} [props.referrer=ADD_NAME_PANEL_REFERRERS.BLANK_STATE] - The source referrer for the panel, determining header details.
 * @param {Object} [props.person] - The initial person object to review or add. May be undefined for new entries.
 * @param {string} [props.recordId] - An existing record id to add the new person to. Optional.
 *
 * @returns {JSX.Element} The rendered panel UI for adding and reviewing person details.
 */
const AddNamesPanel = ({ referrer = ADD_NAME_PANEL_REFERRERS.BLANK_STATE, person, recordId = null }) => {
  const [t] = useTranslation()
  const { images } = useSlsStateContext()
  const { addNamesOverlay, closeOverlay, closeAll } = useOverlayContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const r2State = useR2StateContext()
  const [stepperState, stepperDispatch] = useReducer(stepperReducer, stepperInitialState)
  const [personIdsToReview, setPersonIdsToReview] = useState(person?.id ? [person.id] : [])
  const [currentPersonIndex, setCurrentPersonIndex] = useState(0)
  const saveChangesOverlay = useOverlay()
  const [showSaveChangesOverlay, setShowSaveChangesOverlay] = useState(false)
  const scrollRef = useRef(null)
  const splitScreenPartitionKey = r2State.splitScreen ? thumbnails?.[r2State?.referenceFocusedIndex]?.ark : null

  // Retrieve the SLS person object by id
  // on the first load, if no person id is provided, we assume it's a new primary person
  const slsPerson = useMemo(() => {
    const currentPersonId = personIdsToReview[currentPersonIndex]
    let personObj
    if (!currentPersonId) {
      personObj = { primary: true, recordId: recordId || 'MAKE_NEW_RECORD_ID' }
    } else {
      personObj = getNestedOrFloatingElementById(images, currentPersonId)
    }

    return personObj
  }, [personIdsToReview, currentPersonIndex, images, recordId])

  const headerDetails = getHeaderDetails(referrer)

  const closeAndReset = useCallback(
    (shouldCloseAll = false) => {
      stepperDispatch({ type: 'RESET' })
      setPersonIdsToReview([])
      setCurrentPersonIndex(0)
      if (shouldCloseAll) closeAll()
      else closeOverlay('addNamesOverlay')
    },
    [closeOverlay, closeAll]
  )

  const handleClosePanel = useCallback(
    (shouldCloseAll = false) => {
      if (showSaveChangesOverlay) {
        saveChangesOverlay.open()
      } else {
        closeAndReset(shouldCloseAll)
      }
    },
    [showSaveChangesOverlay, saveChangesOverlay, closeAndReset]
  )

  const handleDirtyChange = useCallback((isDirty) => {
    setShowSaveChangesOverlay(isDirty)
  }, [])

  useUnsavedChangesOverlay({
    overlay: saveChangesOverlay,
    panelId: 'addNamesOverlay',
    condition: showSaveChangesOverlay,
  })

  const familyMembersToReview = useMemo(() => {
    // Get next family members to review based on current person index
    return personIdsToReview
      .slice(currentPersonIndex + 1)
      .map((id) => getNestedOrFloatingElementById(images, id))
      .filter(Boolean)
  }, [personIdsToReview, currentPersonIndex, images])

  const sharedOnSaveCallback = useCallback(
    (currentStep, nextStep) => {
      const actions = [{ type: 'SET_REVIEWED', step: currentStep, value: true }]
      if (stepperState.currentEditStep === currentStep) {
        // Advance to the next step in flow
        actions.push({ type: 'SET_CURRENT_STEP', step: nextStep })
      } else {
        // Close the card and let the reducer figure out what card to open next
        actions.push({ type: 'SET_EDIT_MODE', step: currentStep, value: false })
      }
      stepperDispatch({
        type: 'BATCH',
        actions,
      })

      setShowSaveChangesOverlay(false)
    },
    [stepperState.currentEditStep]
  )

  const isInReviewProcess = currentPersonIndex > 0

  // Display highlights when all cards are in view mode
  const allCardsInViewMode = useMemo(
    () => Object.values(stepperState.editMode).every((isEditing) => !isEditing),
    [stepperState.editMode]
  )

  const fieldIds = useMemo(() => {
    if (!slsPerson?.id) return []
    const fields = getNestedSubelementsByType(slsPerson, 'FIELD')
    return fields.map((field) => field.id).filter(Boolean)
  }, [slsPerson])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: allCardsInViewMode && !!slsPerson?.id, // Only when all cards in view mode and person exists
  })

  return (
    <InfoSheetPanel {...addNamesOverlay} close={() => handleClosePanel()} {...headerDetails}>
      {/* Using the person id as key to force re-render when person changes, so all child components reset their internal state */}
      <FlowGrid columnCount={1} orientation="horizontal" key={slsPerson?.id}>
        <Paragraph size="sm" ref={scrollRef}>
          {t(
            'fullText.addNamesPanel.description',
            'Enter document details like names, relationships, and events for the person you would like to add.'
          )}
        </Paragraph>
        <EssentialInformationCard
          person={slsPerson}
          isPrimaryPerson={isInReviewProcess ? false : slsPerson?.primary || !person?.id}
          reviewed={stepperState.reviewed.essentialInfo}
          editMode={stepperState.editMode[STEPS.ESSENTIAL_INFO]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ESSENTIAL_INFO, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.ESSENTIAL_INFO, value })}
          onSave={(data) => {
            // If this is the first person being added, update the pending review list with the assigned id
            if (currentPersonIndex === 0 && data.personId) {
              setPersonIdsToReview((prev) => [data.personId, ...prev.slice(1)])
            }
            sharedOnSaveCallback(STEPS.ESSENTIAL_INFO, STEPS.FAMILY_MEMBERS)
          }}
          onDirtyChange={handleDirtyChange}
          saveCardDataToSls
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        <FamilyMembersCard
          person={slsPerson}
          reviewed={stepperState.reviewed.familyMembers}
          editMode={stepperState.editMode[STEPS.FAMILY_MEMBERS]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.FAMILY_MEMBERS, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.FAMILY_MEMBERS, value })}
          onSave={(data) => {
            // Add created person ids to pending review
            if (data.personIdsToReview && data.personIdsToReview.length > 0) {
              setPersonIdsToReview((prev) => [...prev, ...data.personIdsToReview])
            }
            sharedOnSaveCallback(STEPS.FAMILY_MEMBERS, STEPS.EVENTS)
          }}
          isInReviewProcess={isInReviewProcess}
          onDirtyChange={handleDirtyChange}
          saveCardDataToSls
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        <EventsCard
          person={slsPerson}
          reviewed={stepperState.reviewed.events}
          editMode={stepperState.editMode[STEPS.EVENTS]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.EVENTS, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.EVENTS, value })}
          onSave={() => {
            sharedOnSaveCallback(STEPS.EVENTS, STEPS.ADDITIONAL_FACTS)
          }}
          saveCardDataToSls
          onDirtyChange={handleDirtyChange}
          showDataInPendingState={isInReviewProcess}
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        <AdditionalFactsCard
          person={slsPerson}
          reviewed={stepperState.reviewed.additionalFacts}
          editMode={stepperState.editMode[STEPS.ADDITIONAL_FACTS]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ADDITIONAL_FACTS, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.ADDITIONAL_FACTS, value })}
          onSave={() => {
            sharedOnSaveCallback(STEPS.ADDITIONAL_FACTS, STEPS.REVIEW_ALL)
          }}
          onDirtyChange={handleDirtyChange}
          saveCardDataToSls
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        <ReviewCard
          active={stepperState.editMode[STEPS.REVIEW_ALL]}
          person={slsPerson}
          familyMembersToReview={familyMembersToReview}
          splitScreenPartitionKey={splitScreenPartitionKey}
          onSave={(action) => {
            if (action === 'continue') {
              if (currentPersonIndex + 1 < personIdsToReview.length) {
                setCurrentPersonIndex((prev) => prev + 1)
                stepperDispatch({ type: 'RESET' })
                scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
              } else {
                handleClosePanel()
              }
            } else if (action === 'attach') {
              const personArk = personIdsToReview[0] || slsPerson.id || slsPerson.recordId
              redirectToSourceLinker(personArk)
              handleClosePanel()
            } else if (action === 'closeAll') {
              handleClosePanel(true)
            } else if (action === 'close') {
              handleClosePanel()
            }
          }}
        />
      </FlowGrid>
      {/* Unsaved Changes Overlay */}
      <UnsavedChangesDialog
        overlay={saveChangesOverlay}
        onExit={() => {
          saveChangesOverlay.close()
          // If there's a pending action, run it after confirming exit, e.g., navigate away or close panel
          if (typeof saveChangesOverlay.pendingAction === 'function') {
            saveChangesOverlay.pendingAction()
          } else {
            closeAndReset()
          }
          closeOverlay('addNamesOverlay')
        }}
      />
    </InfoSheetPanel>
  )
}

export default AddNamesPanel
